import { FunctionComponent, useContext, useState, useRef, useEffect } from "react";
import cx from "classnames";
import { ActionsContext } from "./ActionsContext";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { setBetAmount as storeBetAmount } from "./store/betSlice";
import { updatePublicLobbySettings } from "./Firebase";
import { GameMode } from "./Types";
import { getPlatformConfig } from "./services/FirebaseUsers";

export type MatchPointValue = 1 | 2 | 3;

const DEFAULT_BET_OPTIONS = [0, 50, 100, 250, 500, 1000];
const COMMISSION  = 0.20;

type MatchSettingsMenuProps = {
  roomCode: string | null;
  matchPointsValue: MatchPointValue;
  enableDoubling: boolean;
  onMatchPointsChanged: (m: MatchPointValue) => void;
  onEnableDoublingChanged: (e: boolean) => void;
};

const MatchSettingsMenu: FunctionComponent<MatchSettingsMenuProps> = ({
  roomCode,
  matchPointsValue,
  enableDoubling,
  onMatchPointsChanged,
  onEnableDoublingChanged,
}) => {
  // При изменении очков — тоже синхронизируем
  const handlePointsChange = (v: MatchPointValue) => {
    onMatchPointsChanged(v);
    if (roomCode) updatePublicLobbySettings(roomCode, betAmount, v, enableDoubling, gameMode).catch(() => {});
  };
  const handleDoublingChange = (v: boolean) => {
    onEnableDoublingChanged(v);
    if (roomCode) updatePublicLobbySettings(roomCode, betAmount, matchPointsValue, v, gameMode).catch(() => {});
  };
  const actions     = useContext(ActionsContext);
  const dispatch    = useAppDispatch();
  const userProfile = useAppSelector((s) => s.user.profile);
  const currentGameMode = useAppSelector((s) => s.settings.gameMode);
  const [gameMode, setGameModeLocal] = useState<GameMode>(currentGameMode ?? GameMode.Backgammon);
  const [copied, setCopied] = useState(false);
  const [betAmount, setBetLocal] = useState(0);
  const [customBet, setCustomBet] = useState("");
  const [useCustomBet, setUseCustomBet] = useState(false);
  const [betOptions, setBetOptions] = useState<number[]>(DEFAULT_BET_OPTIONS);
  const [maxBet, setMaxBet] = useState<number>(1000);

  useEffect(() => {
    getPlatformConfig().then(cfg => {
      if (cfg.betOptions && cfg.betOptions.length > 0) {
        const opts = [0, ...cfg.betOptions.filter((v: number) => v > 0)];
        setBetOptions(opts);
      }
      if (typeof cfg.maxBet === "number") setMaxBet(cfg.maxBet);
    }).catch(() => {});
  }, []);

  const coins     = userProfile?.coins ?? 0;
  const canAfford = betAmount === 0 || coins >= betAmount;

  const pot     = betAmount * 2;
  const payout  = Math.floor(pot * (1 - COMMISSION));

  // Обновляем publicLobbies сразу при изменении ставки (пока хост на экране настроек)
  const updateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncPublicLobby = (bet: number, points: MatchPointValue, doubling: boolean) => {
    if (!roomCode) return;
    if (updateTimer.current) clearTimeout(updateTimer.current);
    updateTimer.current = setTimeout(() => {
      updatePublicLobbySettings(roomCode, bet, points, doubling, gameMode).catch(() => {});
    }, 500); // debounce 500ms
  };

  const handleBet = (v: number) => {
    setUseCustomBet(false);
    setCustomBet("");
    setBetLocal(v);
    dispatch(storeBetAmount(v));
    syncPublicLobby(v, matchPointsValue, enableDoubling);
  };

  const handleCustomBet = (val: string) => {
    const n = parseInt(val.replace(/\D/g, ""), 10);
    setCustomBet(val.replace(/\D/g, ""));
    if (!isNaN(n) && n > 0) {
      setUseCustomBet(true);
      setBetLocal(n);
      dispatch(storeBetAmount(n));
      syncPublicLobby(n, matchPointsValue, enableDoubling);
    } else {
      setUseCustomBet(false);
      setBetLocal(0);
      dispatch(storeBetAmount(0));
      syncPublicLobby(0, matchPointsValue, enableDoubling);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(roomCode ?? "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="MS-page">
      <div className="MS-card">
        <h2 className="MS-title">Настройки матча</h2>

        {/* Режим игры */}
        <div className="MS-section">
          <div className="MS-label">Режим игры</div>
          <div className="MS-toggle-row">
            <button
              className={cx("MS-toggle-btn", { active: gameMode === GameMode.Backgammon })}
              onClick={() => setGameModeLocal(GameMode.Backgammon)}
            >
              Короткие
            </button>
            <button
              className={cx("MS-toggle-btn", { active: gameMode === GameMode.LongNardy })}
              onClick={() => setGameModeLocal(GameMode.LongNardy)}
            >
              Длинные
            </button>
          </div>
          {gameMode === GameMode.LongNardy && (
            <div className="MS-bet-info" style={{marginTop:4}}>
              Все шашки стартуют в одной точке · Нельзя бить шашки соперника
            </div>
          )}
        </div>

        {/* До побед */}
        <div className="MS-section">
          <div className="MS-label">До побед</div>
          <div className="MS-points-row">
            {([1, 2, 3] as MatchPointValue[]).map((v) => (
              <button
                key={v}
                className={cx("MS-point-btn", { active: matchPointsValue === v })}
                onClick={() => handlePointsChange(v)}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Куб удвоения */}
        <div className="MS-section">
          <div className="MS-label">Куб удвоения</div>
          <div className="MS-toggle-row">
            <button
              className={cx("MS-toggle-btn", { active: !enableDoubling })}
              onClick={() => handleDoublingChange(false)}
            >
              Выкл
            </button>
            <button
              className={cx("MS-toggle-btn", { active: enableDoubling })}
              onClick={() => handleDoublingChange(true)}
            >
              Вкл
            </button>
          </div>
        </div>

        {/* Ставка (только в онлайн) */}
        {roomCode !== null && (
          <div className="MS-section">
            <div className="MS-label">Ставка</div>
            <div className="MS-bet-row">
              {betOptions.map((v) => {
                const disabled = v > 0 && coins < v;
                return (
                  <button
                    key={v}
                    className={cx("MS-bet-btn", { active: !useCustomBet && betAmount === v, disabled })}
                    disabled={disabled}
                    onClick={() => !disabled && handleBet(v)}
                  >
                    {v === 0 ? "Нет" : `${v} ₽`}
                  </button>
                );
              })}
            </div>
            <div className="MS-custom-bet-row">
              <input
                className="MS-custom-bet-input"
                type="number"
                inputMode="numeric"
                placeholder="Своя сумма ₽"
                value={customBet}
                min={1}
                max={Math.min(coins, maxBet)}
                onChange={(e) => handleCustomBet(e.target.value)}
              />
            </div>
            {betAmount > 0 ? (
              <div className="MS-bet-info">
                Банк: <strong>{pot} ₽</strong>
                {" · "}Победитель получит: <strong>{payout} ₽</strong>
                {" · "}Комиссия: 20%
                {" · "}Баланс: {coins} ₽
              </div>
            ) : (
              <div className="MS-bet-info">Игра без ставки</div>
            )}
          </div>
        )}

        {/* Код комнаты */}
        {roomCode !== null && (
          <div className="MS-section">
            <div className="MS-label">Код комнаты</div>
            <div className="MS-code-row">
              <div className="MS-room-code">{roomCode}</div>
              <button className="MS-copy-btn" onClick={handleCopy}>
                {copied ? "✓" : "Копировать"}
              </button>
            </div>
            <div className="MS-code-hint">Скажи другу этот код — он введёт его в меню</div>
          </div>
        )}

        {/* Кнопка старта */}
        <button
          className="MS-start-btn"
          disabled={!canAfford}
          onClick={() => {
            if (roomCode) {
              updatePublicLobbySettings(roomCode, betAmount, matchPointsValue, enableDoubling, gameMode).catch(() => {});
            }
            actions.updateMatchSettings(matchPointsValue, enableDoubling, gameMode, betAmount);
          }}
        >
          {betAmount > 0 ? `Начать · Ставка ${betAmount} ₽ (выигрыш ${payout} ₽)` : "Начать игру"}
        </button>

        {!canAfford && (
          <div className="MS-error">Недостаточно средств для ставки</div>
        )}
      </div>
    </div>
  );
};

export default MatchSettingsMenu;
